/**
 * Weidner Via6 override of the rise `ScheduleTourForm`.
 *
 * Differs from the base in three ways:
 *  - No background image / parallax — the form sits on a solid theme
 *    primary background instead.
 *  - No info-cards row beneath the form (visit / call / hours tiles).
 *  - Schema is trimmed to just the form block; the bg_image slot group,
 *    address slot group, and visit/call/hours tokens are removed since
 *    they no longer render here.
 */

import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const formSlot = useBlockSlot('scheduletourform.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  return (
    <>
      <div className="stf-section">
        <div className="stf-content">
          <div className="stf-form-wrap">
            <EditableToken path="scheduletourform.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="stf-form-empty-card">
                  <p className="stf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .stf-section {
          position: relative;
          background: var(--vibe-color-primary);
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (min-width: 768px) {
          .stf-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        .stf-content {
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .stf-form-wrap {
          width: 100%;
          max-width: 640px;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        /* Uppercase the block's wizard heading */
        .stf-form-wrap h3 {
          text-transform: uppercase;
        }

        .stf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* ── Form text visibility on white background ──
           The block uses Tailwind utility classes (text-foreground,
           text-muted-foreground, text-primary, text-primary-foreground)
           that resolve to runtime CSS vars. Because the theme's
           --vibe-color-text has been pushed to white, every
           text-foreground element (May 2026, the date numbers, the
           "Times for…" label, the time-slot labels, the "Continue"
           button text) renders white-on-white against the form's white
           card. Pin the text colors inside the form so they stay
           readable regardless of token state. */
        .stf-form-wrap .text-foreground {
          color: #342e1f;
        }
        .stf-form-wrap .text-muted-foreground {
          color: #6b6355;
        }
        .stf-form-wrap .text-primary {
          color: var(--vibe-color-primary);
        }
        .stf-form-wrap .text-primary-foreground {
          color: #ffffff;
        }

        /* ── Time-slot button visibility ──
           Unselected time-slot buttons render bg-background +
           text-foreground inside the form's bg-background card, which
           is white-on-white. Give them a clear surface tint and pin the
           text so they read as buttons. */
        .stf-form-wrap div.grid.gap-2.max-h-60 > button {
          background: var(--vibe-color-surface, #f5f3ef);
          color: #342e1f;
          border-color: var(--vibe-color-border);
        }
        .stf-form-wrap div.grid.gap-2.max-h-60 > button:hover {
          border-color: var(--vibe-color-primary);
        }
        .stf-form-wrap div.grid.gap-2.max-h-60 > button.bg-primary {
          background: var(--vibe-color-primary);
          color: #ffffff;
          border-color: var(--vibe-color-primary);
        }
        /* Compact-mode <select> renders white-on-white the same way. */
        .stf-form-wrap select {
          background: var(--vibe-color-surface, #f5f3ef);
          color: #342e1f;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
